import { Link, useParams } from 'react-router';

import { TwPicture } from '@boc/images';
import { getAttributeValue } from '@boc/framework/products/attributes';
import type { ProductCardFragment } from '@boc/framework/saleor-graphql';
import { cn } from '@boc/ui';

import { imgUrl, urls } from '~/urls';

import { Price } from '~/local/components/price';
import { ProductTag } from '~/local/products/details/components/product-tag';

import { isOwnProduct } from '#/products/own';
import { genProductCategoryTitle, genProductTitle } from '#/products/seo';

const ProductCardDetails = ({ product }: { product: ProductCardFragment }) => {
  switch (product.productType.slug) {
    case 'jigsaw-puzzle': {
      const publisher = getAttributeValue(product.attributes, 'puzzle-publisher');

      return (
        <>
          <h5 className="text-base font-medium">{genProductCategoryTitle(product)}</h5>
          <div className="space-y-1">
            <h4 className="font-heading text-medium leading-5 tracking-tight">{product.name}</h4>
            {publisher ? <h5 className="font-heading text-base leading-3">{publisher}</h5> : <></>}
          </div>
          <div className="text-medium space-x-2 leading-5 font-medium tracking-wide">
            {product.pricing?.onSale ? (
              <Price
                price={product.pricing?.priceRangeUndiscounted?.start?.gross}
                className="line-through"
              />
            ) : (
              <></>
            )}
            <Price price={product.pricing?.priceRange?.start?.gross} />
          </div>
        </>
      );
    }
    default:
      return (
        <>
          <h5 className="font-medium">{genProductCategoryTitle(product)}</h5>
          <h4 className="font-heading text-medium leading-5 tracking-tight">{product.name}</h4>
          <div className="text-medium leading-5 font-medium tracking-wide">
            <Price price={product.pricing?.priceRange?.start?.gross} />
          </div>
        </>
      );
  }
};

function ProductCardInner({ product }: { product: ProductCardFragment }) {
  const src = product.media?.[0]?.url;
  const alt = `${genProductTitle(product)} - Thumbnail Image`;

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'bg-primary-2-20 relative flex aspect-square items-center',
          !product.isAvailable && 'opacity-60'
        )}
      >
        <ProductTag product={product} showDiscount />
        <div className="mx-auto h-full w-full p-4">
          {src && (
            <TwPicture
              imgUrl={imgUrl}
              src={src}
              defaultSize={181}
              twSizes={{
                sm: 250,
                md: 310,
                lg: 250,
                xl: 310,
              }}
              alt={alt}
              className="mx-auto h-full w-full object-contain mix-blend-multiply"
            />
          )}
        </div>
        {isOwnProduct(product) && (
          <img
            className="absolute bottom-2 left-2 w-10 sm:bottom-3 sm:left-3 sm:w-12 md:bottom-4 md:left-4 md:w-14 lg:w-16 xl:w-20"
            src="/static/jsn-og-sticker.svg"
            alt={`JSN Original ${product.category?.name}`}
          />
        )}
      </div>
      <div className="text-primary-2-80 flex flex-col items-center gap-y-3 py-6 text-center">
        <ProductCardDetails product={product} />
      </div>
    </div>
  );
}

function ProductCard({
  product,
  prefetch = false,
}: {
  product: ProductCardFragment;
  prefetch?: boolean;
}) {
  const params = useParams();

  return (
    <>
      <Link
        to={urls.products.details(params, product)}
        prefetch={prefetch ? 'render' : 'intent'}
        className="hidden h-full lg:block"
      >
        <ProductCardInner product={product} />
      </Link>
      <Link
        to={urls.products.details(params, product)}
        prefetch={prefetch ? 'render' : 'viewport'}
        className="h-full lg:hidden"
      >
        <ProductCardInner product={product} />
      </Link>
    </>
  );
}

export default ProductCard;
